import { FormControl, Select, MenuItem, Box, Typography } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';

const CustomSelect = ({
    label,
    value,
    onChange,
    options = [],
    getOptionLabel = (option) => option,
    getOptionValue = (option) => option,
    placeholder = 'Select an option',
    required = false,
    error = false,
    helperText = '',
    disabled = false,
    fullWidth = false,
    ...props
}) => {
    return (
        <Box sx={{ minWidth: fullWidth ? 0 : 225, flex: fullWidth ? 1 : '0 0 auto', width: fullWidth ? '100%' : 'auto' }}>
            {label && (
                <Typography
                    sx={{
                        fontSize: '16px',
                        fontWeight: 500,
                        color: 'var(--forbrand-primary)',
                        mb: 1,
                        lineHeight: '140%'
                    }}
                >
                    {label}
                    {required && <span style={{ color: 'var(--forbrand-error)' }}>*</span>}
                </Typography>
            )}
            <FormControl fullWidth size="small" error={error} disabled={disabled}>
                <Select
                    value={value || ''}
                    onChange={onChange}
                    IconComponent={KeyboardArrowDown}
                    displayEmpty
                    renderValue={(selected) => {
                        if (!selected || selected === '') {
                            return <span style={{ color: '#999' }}>{placeholder}</span>
                        }
                        const selectedOption = options.find(opt => getOptionValue(opt) === selected)
                        return selectedOption ? getOptionLabel(selectedOption) : selected
                    }}
                    sx={{
                        backgroundColor: disabled ? 'var(--bg-disabled)' : '#fff',
                        fontSize: '14px',
                        height: '48px',
                        width: fullWidth ? '100%' : '225px',
                        borderRadius: 'unset',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: error ? 'var(--forbrand-error)' : '#d0d0d0',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: error ? 'var(--forbrand-error)' : '#a0a0a0',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: error ? 'var(--forbrand-error)' : 'var(--forbrand-color)',
                        },
                        '& .MuiSelect-select': {
                            fontSize: '14px',
                            color: '#374151',
                            // Keep the selected value on a single line with an ellipsis so
                            // the trigger height stays fixed. Long option text is still fully
                            // visible in the dropdown popup below.
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        },
                        '& .MuiSelect-icon': {
                            color: '#6b7280',
                            fontSize: '20px',
                        },
                        '&.Mui-disabled': {
                            backgroundColor: 'var(--bg-disabled)',
                        }
                    }}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                border: '1px solid var(--border-strong)',
                                mt: 0.5,
                                maxHeight: '250px',
                                width: '225px'
                            }
                        }
                    }}
                    {...props}
                >
                    {placeholder && (
                        <MenuItem value="" disabled>
                            <span style={{ color: '#999' }}>{placeholder}</span>
                        </MenuItem>
                    )}
                    {options.map((option, index) => (
                        <MenuItem
                            key={index}
                            value={getOptionValue(option)}
                            sx={{
                                fontSize: '14px',
                                color: '#374151',
                                py: 1.5,
                                px: 2,
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                '&:hover': {
                                    backgroundColor: '#f9fafb',
                                },
                                '&.Mui-selected': {
                                    backgroundColor: '#f3f4f6',
                                    '&:hover': {
                                        backgroundColor: '#e5e7eb',
                                    }
                                }
                            }}
                        >
                            {getOptionLabel(option)}
                        </MenuItem>
                    ))}
                </Select>
                {helperText && (
                    <Typography
                        variant="caption"
                        sx={{
                            color: error ? '#d32f2f' : '#666',
                            mt: 0.5,
                            ml: 1.75,
                            fontSize: '0.75rem',
                        }}
                    >
                        {helperText}
                    </Typography>
                )}
            </FormControl>
        </Box>
    );
}

export default CustomSelect;